import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ScrollView, InteractionManager, StatusBar } from "react-native";
import {
  getReviewsRequest,
  getUserRankRequest,
} from "@microdoing/shared/services/userCampaign/actions";
import ProgressChart from "./ProgressChart";
import Ranking from "./Ranking";
import { Loading, MainTitle } from "app/components/common";
import {
  getCampaignId,
  getCampaignSteps,
  getHasCampaignReviews,
  getIsCoachingCampaign,
  getIsRankingDisabled,
  getUserCampaignId,
} from "@microdoing/shared/services/userCampaign/selectors";
import CampaignProgress from "./Components/CampaignProgress";
import CampaignReviews from "./CampaignReviews";
import { Box } from "app/components/common";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import theme from "app/Theme/Theme";
import { breadCrumbsProgressScreen } from "app/navigation/breadcrumb/BreadcrumbPaths";
import WebDesktopView from "app/components/common/Web/WebDesktopView";
import HeaderTabBar from "app/navigation/TabBar/HeaderTabBar";
import i18n from "@microdoing/shared/i18n";
import HistoricalCalendar from "app/scenes/UserProfile/HistoricalCalendar";
import { getUser } from "@microdoing/shared/services/user/selectors";
import UserStats from "app/scenes/UserProfile/UserStats/UserStats";
import ProgressInfos from "./Components/ProgressInfos";
import SeeAll from "app/components/common/SeeAll";
import { changeNavField } from "@microdoing/shared/services/nav/actions";
import { getScrollTop } from "@microdoing/shared/services/nav/selectors";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import {
  SafeAreaView,
  useSafeAreaInsets,
} from "react-native-safe-area-context";
import {
  IncrementalGroup,
  Incremental,
} from "@th3rdwave/react-native-incremental";

const ProgressScreen = ({}) => {
  const [screenLoading, setScreenLoading] = useState(false);
  const [widthScreen, setWidthScreen] = useState(0);

  const steps = useSelector(getCampaignSteps);
  const isCoachingCampaign = useSelector(getIsCoachingCampaign);
  const isRankingDisabled = useSelector(getIsRankingDisabled);
  const hasReviews = useSelector(getHasCampaignReviews);
  const user = useSelector(getUser);
  const scrollViewRef = useRef(null);
  const shouldScrollTop = useSelector(getScrollTop);
  const campaignId = useSelector(getCampaignId);
  const userCampaignId = useSelector(getUserCampaignId);

  const navigation = useNavigation();

  const insets = useSafeAreaInsets();

  const { goals, hasGoals, skills, achievements, categories, _id } = user;

  const dispatch = useDispatch();

  useFocusEffect(
    React.useCallback(() => {
      dispatch(getUserRankRequest({ userCampaignId, campaignId }));
    }, [userCampaignId, campaignId]),
  );

  useEffect(() => {
    scrollTop();
    dispatch(changeNavField({ scrollTop: false }));
  }, [shouldScrollTop]);

  const scrollTop = () => {
    if (scrollViewRef && scrollViewRef.current) {
      scrollViewRef?.current?.scrollTo({ x: 0, y: 0, animated: true });
    }
  };

  useEffect(() => {
    dispatch(getReviewsRequest());
    InteractionManager.runAfterInteractions(() => {
      // 2: Component is done animating
      // 3: Start fetching data that is needed to render UI
      setScreenLoading(false); //Set screenloading prop to false
    });
  }, []);

  const findDimensionsScreen = ({ width }) => {
    setWidthScreen(width);
  };

  const handleOnLayoutScreen = event => {
    findDimensionsScreen(event.nativeEvent.layout);
  };

  const getTotalSkillsPoints = useCallback(() => {
    const total = skills.reduce((prev, curr) => {
      return prev + curr.experience;
    }, 0);

    return total;
  }, [skills]);

  if (isWebDesktop) {
    return (
      <WebDesktopView breadcrumbsPath={breadCrumbsProgressScreen}>
        <ScrollView
          contentContainerStyle={styles.scrollView}
          style={{ height: "100%", flex: 1 }}
          testID="progress-screen-scrollView"
        >
          <Box
            flexDirection="row"
            style={{ columnGap: theme.spacing.xm }}
            width="100%"
            onLayout={handleOnLayoutScreen}
          >
            <Box width={widthScreen / 2 - theme.spacing.xm / 2}>
              <CampaignProgress />
              <ProgressInfos />
              <Ranking />
            </Box>
            {!isCoachingCampaign && !isRankingDisabled && (
              <Box width={widthScreen / 2 - theme.spacing.xm / 2}>
                <Box
                  backgroundColor="neutral.white"
                  borderRadius={8}
                  marginBottom="sm"
                  paddingVertical="xm"
                >
                  <Box
                    alignItems="center"
                    justifyContent="space-between"
                    flexDirection="row"
                    paddingHorizontal="xm"
                    marginBottom="xl"
                  >
                    <MainTitle variant="l6-bold">{i18n.t("Action")}</MainTitle>
                    <SeeAll navigationRoute="HistoricalList" />
                  </Box>
                  {!!hasReviews && (
                    <>
                      <CampaignReviews />
                      <Box
                        borderTopWidth={1}
                        borderTopColor="neutral.75"
                        marginVertical="xm"
                      />
                    </>
                  )}
                  {screenLoading ? (
                    <Loading size={35} />
                  ) : (
                    <Box width="100%">
                      <HistoricalCalendar navigation={navigation} />
                      <Box
                        borderTopColor="neutral.75"
                        borderTopWidth={1}
                        marginVertical="xm"
                      />
                      <ProgressChart data={steps} />
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </ScrollView>
      </WebDesktopView>
    );
  }

  return (
    <WebDesktopView breadcrumbsPath={breadCrumbsProgressScreen}>
      <Box
        style={{
          flex: 1,
          paddingHorizontal: theme.spacing.sm,
          paddingTop: insets.top,
        }}
      >
        <StatusBar
          animated
          barStyle="dark-content"
          backgroundColor={theme.colors["neutral.white"]}
        />

        <HeaderTabBar
          title={
            <MainTitle variant="l8-bold">
              {i18n.t("breadcrumb-progress")}
            </MainTitle>
          }
        />
        {screenLoading && <Loading size={35} />}
        <ScrollView
          ref={scrollViewRef}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollView}
          style={{ flex: 1, width: "100%" }}
          testID="progress-screen-scrollView"
        >
          <IncrementalGroup name="detail_content">
            <CampaignProgress />
            <ProgressInfos />
            <Box
              backgroundColor="neutral.white"
              borderRadius={8}
              marginBottom="sm"
              paddingVertical="xm"
              width="100%"
            >
              <Box
                alignItems="center"
                justifyContent="space-between"
                flexDirection="row"
                paddingHorizontal="xm"
                marginBottom="xl"
              >
                <MainTitle variant="l6-bold">{i18n.t("Reviews")}</MainTitle>
                <SeeAll navigationRoute="HistoricalList" />
              </Box>
              {!!hasReviews && (
                <>
                  <CampaignReviews />
                  <Box
                    borderTopWidth={1}
                    borderTopColor="neutral.75"
                    marginVertical="xm"
                  />
                </>
              )}

              <Incremental>
                <Box width="100%">
                  <HistoricalCalendar />
                  <Box
                    borderTopColor="neutral.75"
                    borderTopWidth={1}
                    marginVertical="xm"
                  />
                  <ProgressChart data={steps} />
                </Box>
              </Incremental>
            </Box>
            <Incremental>
              {!isCoachingCampaign && !isRankingDisabled && (
                <Box width="100%">
                  {getTotalSkillsPoints() > 0 && (
                    <UserStats
                      skills={skills}
                      goals={goals}
                      hasGoals={hasGoals}
                      categories={categories}
                      totalSkillsPoints={getTotalSkillsPoints()}
                    />
                  )}
                  <Box marginTop="xxs">
                    <Ranking />
                  </Box>
                </Box>
              )}
            </Incremental>
          </IncrementalGroup>
        </ScrollView>
      </Box>
    </WebDesktopView>
  );
};

const styles = {
  scrollView: {
    display: "flex",
    paddingBottom: !isWebDesktop ? theme.spacing.xm : 0,
    width: "100%",
    columnGap: isWebDesktop ? theme.spacing.xm : 0,
    flexDirection: isWebDesktop ? "row" : "column",
  },
};

export default React.memo(ProgressScreen);
